import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icons.jsx';
import { friendlyErrorMessage, notify } from '@/lib/notifications';
import { PlatformIcon } from './shared/PlatformIcon.jsx';
import { PLATFORM_LABELS } from './shared/platformLabels.js';
import { api } from '../../services/api/client.js';
import { aggregateMetricSeries, formatMetric, metricNumber, normalizeMetrics, reactionField } from '../../lib/socialMetrics.js';
import { MetricValue } from '../../components/metrics/MetricValue.jsx';

const WINDOW_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

export function AnalyticsTab({ platformPrefs = [], onJumpTo }) {
  const { t } = useTranslation();
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [missingEndpoint, setMissingEndpoint] = useState(false);
  const [recommendationStates, setRecommendationStates] = useState({});
  const [readError, setReadError] = useState(false);
  const requestRef = useRef(0);
  const scope = api.getReadScope();

  async function load(window) {
    const request = ++requestRef.current;
    const current = () => request === requestRef.current && scope === api.getReadScope();
    try {
      setLoading(true);
      setReadError(false);
      const result = await api.analytics.getInsights({ windowDays: window });
      if (!current()) return;
      const payload = result.insights || result;
      setInsights(payload || null);
      setMissingEndpoint(false);
    } catch (err) {
      if (!current()) return;
      setReadError(true);
      if (err?.status === 404 || err?.code === 'NOT_IMPLEMENTED') {
        setMissingEndpoint(true);
        setInsights(null);
      } else {
        notify('analytics.loadFailed', { error: err });
      }
    } finally {
      if (current()) setLoading(false);
    }
  }

  useEffect(() => {
    setInsights(null);
    load(windowDays);
    return () => { requestRef.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDays, scope]);

  const platformSummaries = insights?.platformSummaries || [];
  const topPosts = insights?.topPosts || [];
  const warnings = insights?.warnings || [];
  const recommendations = (insights?.recommendations || []).filter((item) => recommendationStates[item.id]?.status !== 'dismissed');

  const kpis = useMemo(() => buildKpis(platformSummaries, t), [platformSummaries, t]);
  const reachRows = useMemo(() => buildMetricRows(platformSummaries, 'medianViews'), [platformSummaries]);
  const reactionRows = useMemo(() => buildMetricRows(platformSummaries, 'medianReactions'), [platformSummaries]);

  return (
    <>
      <div className="card ds-card-pad settings-card">
        <div
          className="settings-card-header"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}
        >
          <div>
            <h3 className="settings-card-title">{t('analyticsTab.performance')}</h3>
            <p className="settings-card-description">{t('analyticsTab.performanceDescription')}</p>
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="cp-window-toggle" role="group" aria-label={t('analyticsTab.timeWindow')}>
              {WINDOW_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={'cp-window-btn ' + (windowDays === opt.value ? 'is-active' : '')}
                  onClick={() => setWindowDays(opt.value)}
                >
                  {t('analyticsTab.daysOption', { count: opt.value })}
                </button>
              ))}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => load(windowDays)} disabled={loading}>
              <Icon name="refresh" size={13} /> {loading ? t('analyticsTab.refreshing') : t('analyticsTab.refresh')}
            </button>
          </div>
        </div>

        {readError && <p className="metric-feedback" role="status">{t(insights ? 'socialMetrics.stale' : 'socialMetrics.unavailable')}</p>}
        {loading ? (
          <KpiSkeleton />
        ) : missingEndpoint ? (
          <PendingState />
        ) : !platformSummaries.length ? (
          <EmptyState onJumpTo={onJumpTo} />
        ) : (
          <>
            <div className="ap-stats-grid cp-kpi-grid">
              {kpis.map((kpi) => (
                <KpiCard key={kpi.label} {...kpi} />
              ))}
            </div>
            {warnings.length > 0 && (
              <div className="cp-warning-row">
                {warnings.map((w, i) => (
                  <div key={i} className="auth-error" style={{ marginTop: 12 }}>
                    <Icon name="alert" size={14} /> {w}
                  </div>
                ))}
              </div>
            )}
            {insights?.summary && (
              <div className="cp-insight-summary">
                <div className="settings-section-label">{t('analyticsTab.summary')}</div>
                <p className="body" style={{ margin: 0 }}>{insights.summary}</p>
              </div>
            )}
            {recommendations.length > 0 && (
              <RecommendationPanel
                recommendations={recommendations}
                states={recommendationStates}
                setStates={setRecommendationStates}
                windowDays={windowDays}
              />
            )}
            <PerformanceCharts reachRows={reachRows} reactionRows={reactionRows} />
          </>
        )}
      </div>

      {!loading && !missingEndpoint && platformSummaries.length > 0 && (
        <div className="card ds-card-pad settings-card">
          <div className="settings-card-header">
            <h3 className="settings-card-title">{t('analyticsTab.perPlatformBreakdown')}</h3>
            <p className="settings-card-description">{t('analyticsTab.perPlatformBreakdownDescription')}</p>
          </div>
          <div className="cp-platform-stats">
            {platformSummaries.map((row) => (
              <PlatformStatCard key={row.platform} row={row} prefs={platformPrefs.find((p) => p.platform === row.platform)} />
            ))}
          </div>
        </div>
      )}

      {!loading && !missingEndpoint && topPosts.length > 0 && (
        <div className="card ds-card-pad settings-card">
          <div className="settings-card-header">
            <h3 className="settings-card-title">{t('analyticsTab.topPosts')}</h3>
            <p className="settings-card-description">{t('analyticsTab.topPostsDescription')}</p>
          </div>
          <div className="cp-top-posts">
            {topPosts.slice(0, 6).map((post, i) => (
              <TopPostRow key={post.contentId || i} post={post} rank={i + 1} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function RecommendationPanel({ recommendations, states, setStates, windowDays }) {
  const { t } = useTranslation();
  return (
    <section className="cp-recommendation-panel">
      <div className="settings-section-label">{t('analyticsTab.recommendedNextActions')}</div>
      <div className="cp-recommendation-grid">
        {recommendations.slice(0, 6).map((recommendation) => (
          <RecommendationCard
            key={recommendation.id}
            recommendation={recommendation}
            state={states[recommendation.id] || {}}
            setState={(next) => setStates(prev => ({ ...prev, [recommendation.id]: { ...(prev[recommendation.id] || {}), ...next } }))}
            windowDays={windowDays}
          />
        ))}
      </div>
    </section>
  );
}

function RecommendationCard({ recommendation, state, setState, windowDays }) {
  const { t } = useTranslation();
  const action = recommendation.assistantAction;
  const limitedData = recommendation.limitedData === true;
  const busy = state.status === 'confirming' || state.status === 'notifying' || state.status === 'asking';

  async function confirm() {
    if (!action?.type) return;
    setState({ status: 'confirming', error: null });
    try {
      const result = await api.assistant.confirmAction({ action, confirmed: true });
      setState({ status: 'confirmed', confirmation: result, error: null });
    } catch (err) {
      setState({ status: 'error', error: friendlyErrorMessage(err, t('analyticsTab.confirmationFailed')) });
    }
  }

  async function notify() {
    setState({ status: 'notifying', error: null });
    try {
      await api.analytics.createRecommendationNotifications({ windowDays, recommendationIds: [recommendation.id] });
      setState({ status: 'notified', error: null });
    } catch (err) {
      setState({ status: 'error', error: friendlyErrorMessage(err, t('analyticsTab.couldNotCreateNotification')) });
    }
  }

  async function dismiss() {
    setState({ status: 'dismissed', error: null });
    try {
      await api.notifications.dismissRecommendation(recommendation.id);
    } catch {
      // Local dismissal still stands; backend may not have created a notification yet.
    }
  }

  async function askWhy() {
    setState({ status: 'asking', error: null });
    try {
      const response = await api.assistant.chat({
        message: `Explain the reasoning for this analytics recommendation without guaranteeing performance: ${recommendation.title}. ${recommendation.reason}`,
        currentScreen: 'analytics',
        selectedEntity: { type: 'analyticsRecommendation', id: recommendation.id }
      });
      setState({ status: 'explained', explanation: response?.message || t('analyticsTab.noExplanationReturned'), error: null });
    } catch (err) {
      setState({ status: 'error', error: friendlyErrorMessage(err, t('analyticsTab.askAiCouldNotExplain')) });
    }
  }

  return (
    <article className="cp-recommendation-card">
      <div className="cp-recommendation-head">
        <div>
          <h4>{recommendation.title}</h4>
          <p>{recommendation.message}</p>
        </div>
        <span className={'cp-confidence-pill is-' + (recommendation.confidence || 'low')}>{t('analyticsTab.confidenceLevel', { level: recommendation.confidence || 'low' })}</span>
      </div>
      <div className="cp-recommendation-reason">
        <Icon name="sparkles" size={13} />
        <span>{recommendation.reason}</span>
      </div>
      <div className="cp-recommendation-meta">
        <span>{t('analyticsTab.type', { type: formatRecommendationType(recommendation.type) })}</span>
        <span>{t('analyticsTab.sample', { count: recommendation.sampleSize || 0 })}</span>
        {limitedData && <span>{t('analyticsTab.limitedData')}</span>}
      </div>
      {limitedData && (
        <div className="cp-limited-data-note">{t('analyticsTab.limitedDataNote')}</div>
      )}
      <div className="cp-recommendation-actions">
        <button className="btn btn-primary btn-sm" onClick={confirm} disabled={busy || !action?.type}>
          {state.status === 'confirming' ? t('analyticsTab.confirming') : t('analyticsTab.confirmAction')}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={askWhy} disabled={busy}>{t('analyticsTab.askAiWhy')}</button>
        <button className="btn btn-ghost btn-sm" onClick={notify} disabled={busy}>{state.status === 'notifying' ? t('analyticsTab.creating') : t('analyticsTab.notifyMe')}</button>
        <button className="btn btn-ghost btn-sm" onClick={dismiss} disabled={busy}>{t('analyticsTab.dismiss')}</button>
      </div>
      {state.status === 'confirmed' && (
        <div className="cp-recommendation-result">
          {state.confirmation?.executed
            ? (state.confirmation.message || t('analyticsTab.backendExecuted'))
            : (state.confirmation?.message || t('analyticsTab.backendValidated'))}
        </div>
      )}
      {state.status === 'notified' && <div className="cp-recommendation-result">{t('analyticsTab.smartNotificationCreated')}</div>}
      {state.explanation && <div className="cp-recommendation-explanation">{state.explanation}</div>}
      {state.error && <div className="auth-error" style={{ marginTop: 10 }}>{state.error}</div>}
    </article>
  );
}

function KpiCard({ label, value, delta, tone = 'green', icon }) {
  return (
    <div className="card ap-stat-card cp-kpi-card">
      <div className="row between" style={{ alignItems: 'center' }}>
        <div className="eyebrow">{label}</div>
        {icon && (
          <span className="cp-kpi-icon" aria-hidden="true">
            <Icon name={icon} size={14} />
          </span>
        )}
      </div>
      <div className="h1" style={{ marginTop: 8, fontSize: 28, lineHeight: 1.1 }}>{value}</div>
      {delta && <div className={'caption tone-' + tone} style={{ marginTop: 6 }}>{delta}</div>}
    </div>
  );
}

function PerformanceCharts({ reachRows, reactionRows }) {
  const { t } = useTranslation();
  if (!reachRows.length) return null;
  return (
    <div className="cp-chart-grid" aria-label={t('analyticsTab.performanceCharts')}>
      <PlatformMetricChart rows={reachRows} label={t('analyticsTab.medianViews')} />
      <PlatformMetricChart rows={reactionRows} label={t('analyticsTab.medianReactions')} />
    </div>
  );
}

function PlatformMetricChart({ rows, label: metricLabel }) {
  const { t } = useTranslation();
  const max = Math.max(...rows.map((row) => row.value), 1);
  const height = 76 + rows.length * 34;

  return (
    <article className="cp-chart-card">
      <div className="cp-chart-head">
        <div>
          <div className="settings-section-label">{metricLabel}</div>
          <p className="caption muted">{t('socialMetrics.snapshotHint')} {t('socialMetrics.chartHint')}</p>
        </div>
      </div>
      <svg
        className="cp-chart-svg"
        viewBox={`0 0 640 ${height}`}
        role="img"
        aria-label={metricLabel}
      >
        <line className="cp-chart-axis" x1="160" y1="42" x2="160" y2={height - 20} />
        {rows.map((row, i) => {
          const y = 58 + i * 34;
          const width = row.value > 0 ? Math.max(4, (row.value / max) * 342) : 0;
          const label = row.label.length > 17 ? `${row.label.slice(0, 16)}...` : row.label;
          return (
            <g key={row.platform}>
              <text className="cp-chart-label" x="20" y={y}>{label}</text>
              <rect className="cp-chart-track-svg" x="160" y={y - 13} width="342" height="16" rx="8" />
              {row.value !== null && <rect className="cp-chart-bar cp-chart-bar-violet" x="160" y={y - 13} width={width} height="16" rx="8" />}
              <text className="cp-chart-value" x="620" y={y}>{formatNumber(row.value)}</text>
            </g>
          );
        })}
      </svg>
    </article>
  );
}

function PlatformStatCard({ row, prefs }) {
  const { t } = useTranslation();
  const enabled = !!prefs?.enabled;
  const engagementPct = formatRate(row.reactionRate ?? row.engagementRate);
  const positivePct = formatRate(row.positiveReactionRate);
  const commentPct = formatRate(row.commentRate);

  return (
    <div className={'cp-platform-stat ' + (enabled ? '' : 'is-disabled')}>
      <div className="cp-platform-stat-head">
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <PlatformIcon platform={row.platform} size={18} />
          <strong>{PLATFORM_LABELS[row.platform] || row.platform}</strong>
        </div>
        <span className="caption muted">{t('analyticsTab.postsCount', { count: row.postCount ?? 0 })}</span>
      </div>
      <div className="cp-platform-stat-grid">
        <Metric label={t('analyticsTab.medianViews')} value={formatNumber(row.medianViews)} />
        <Metric label={t('analyticsTab.medianReactions')} value={formatNumber(row.medianReactions)} />
        <Metric label={t('analyticsTab.medianComments')} value={formatNumber(row.medianComments)} />
      </div>
      <div className="cp-platform-stat-bars">
        <RateBar label={t('analyticsTab.engagement')} pct={engagementPct} tone="violet" />
        <RateBar label={t('analyticsTab.positiveSentiment')} pct={positivePct} tone="green" />
        <RateBar label={t('analyticsTab.comments')} pct={commentPct} tone="blue" />
      </div>
      {Array.isArray(row.notes) && row.notes.length > 0 && (
        <ul className="cp-bullet-list cp-platform-stat-notes">
          {row.notes.slice(0, 3).map((note, i) => (
            <li key={i}>{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="cp-metric">
      <div className="caption muted">{label}</div>
      <div className="cp-metric-value">{value}</div>
    </div>
  );
}

function RateBar({ label, pct, tone }) {
  const safe = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <div className="cp-rate-bar">
      <div className="cp-rate-bar-label">
        <span>{label}</span>
        <span className="caption muted">{pct == null ? '—' : `${safe.toFixed(1)}%`}</span>
      </div>
      <div className="cp-rate-bar-track">
        <div className={'cp-rate-bar-fill cp-rate-bar-fill-' + tone} style={{ width: `${safe}%` }} />
      </div>
    </div>
  );
}

function TopPostRow({ post, rank }) {
  const { t } = useTranslation();
  const metrics = normalizeMetrics(post.metrics || post, post.metricFetch);

  return (
    <div className="cp-top-post">
      <div className="cp-top-post-rank">#{rank}</div>
      <div className="cp-top-post-body">
        <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {post.platform && <PlatformIcon platform={post.platform} size={14} />}
          {post.platform && (
            <span className="chip" style={{ padding: '2px 8px', fontSize: 11 }}>
              {PLATFORM_LABELS[post.platform] || post.platform}
            </span>
          )}
          {post.topic && <strong className="cp-top-post-topic">{post.topic}</strong>}
        </div>
        {post.hook && <p className="caption muted cp-top-post-hook">{post.hook}</p>}
        <div className="cp-top-post-metrics">
          {['views', reactionField(metrics, post.platform), 'comments', 'shares',
            ...(metrics.impressions !== null ? ['impressions'] : [])].map(field => (
            <span className="cp-top-post-stat" key={field}>
              <span>{t(`socialMetrics.${field}`)}</span> <MetricValue metrics={metrics} field={field} detail />
            </span>
          ))}
        </div>
        {post.whyItWorked && (
          <blockquote className="cp-top-post-why">
            <Icon name="sparkles" size={12} />
            <span>{post.whyItWorked}</span>
          </blockquote>
        )}
      </div>
    </div>
  );
}

function KpiSkeleton() {
  return (
    <div className="ap-stats-grid cp-kpi-grid">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="card ap-stat-card cp-kpi-card cp-kpi-skeleton" aria-hidden="true">
          <div className="cp-skeleton-line" style={{ width: 80 }} />
          <div className="cp-skeleton-line cp-skeleton-line-lg" style={{ marginTop: 10 }} />
          <div className="cp-skeleton-line" style={{ width: 60, marginTop: 8 }} />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onJumpTo }) {
  const { t } = useTranslation();
  return (
    <div className="cp-empty-state">
      <div className="cp-empty-icon" aria-hidden="true">
        <Icon name="target" size={20} />
      </div>
      <h4 className="h3" style={{ margin: '12px 0 4px' }}>{t('analyticsTab.noInsightsYet')}</h4>
      <p className="caption muted" style={{ maxWidth: 420 }}>
        {t('analyticsTab.noInsightsDescription')}
      </p>
      <div className="row" style={{ gap: 8, marginTop: 16 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => onJumpTo?.('rules')}>
          <Icon name="plug" size={12} /> {t('analyticsTab.configurePlatforms')}
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => onJumpTo?.('month')}>
          <Icon name="calendar" size={12} /> {t('analyticsTab.planYourMonth')}
        </button>
      </div>
    </div>
  );
}

function PendingState() {
  const { t } = useTranslation();
  return (
    <div className="cp-empty-state">
      <div className="cp-empty-icon" aria-hidden="true">
        <Icon name="info" size={20} />
      </div>
      <h4 className="h3" style={{ margin: '12px 0 4px' }}>{t('analyticsTab.analyticsArrivingSoon')}</h4>
      <p className="caption muted" style={{ maxWidth: 460 }}>
        {t('analyticsTab.analyticsArrivingSoonDescription')}
      </p>
    </div>
  );
}

function buildMetricRows(platformSummaries, field) {
  return platformSummaries
    .map((row) => {
      const posts = Number(row.postCount) || 0;
      return {
        platform: row.platform,
        label: PLATFORM_LABELS[row.platform] || row.platform,
        value: metricNumber(row[field]),
        posts,
      };
    })
    .filter((row) => row.posts > 0 || row.value > 0)
    .sort((a, b) => (b.value ?? -1) - (a.value ?? -1))
    .slice(0, 6);
}

function buildKpis(platformSummaries, t) {
  if (!platformSummaries.length) return [];

  let totalPosts = 0;
  let weightedRate = 0;
  let weightSum = 0;
  let topPlatform = null;
  let topPlatformReach = -1;

  for (const row of platformSummaries) {
    const posts = Number(row.postCount) || 0;
    totalPosts += posts;
    if (row.reactionRate != null && posts > 0) {
      weightedRate += Number(row.reactionRate) * posts;
      weightSum += posts;
    }
    const views = metricNumber(row.medianViews);
    if (views !== null && views > topPlatformReach) {
      topPlatformReach = views;
      topPlatform = row.platform;
    }
  }

  const avgEngagement = weightSum > 0 ? (weightedRate / weightSum) : null;
  const totals = aggregateMetricSeries(platformSummaries.map(row => ({ views: row.totalViews, postCount: row.postCount })));

  return [
    {
      label: t('socialMetrics.views'),
      value: <MetricValue metrics={totals} field="views" />,
      delta: t('socialMetrics.observedOnly'),
      tone: 'purple',
      icon: 'eye',
    },
    {
      label: t('engagementChart.postsPublished'),
      value: formatNumber(totalPosts),
      delta: t('socialMetrics.snapshotHint'),
      tone: 'violet',
      icon: 'heart',
    },
    {
      label: t('postPreview.engagementRate'),
      value: avgEngagement == null ? '—' : `${(avgEngagement * 100).toFixed(1)}%`,
      delta: t(avgEngagement == null ? 'socialMetrics.unavailable' : 'socialMetrics.weightedRate'),
      tone: 'green',
      icon: 'flame',
    },
    {
      label: t('socialMetrics.topPlatform'),
      value: topPlatform ? PLATFORM_LABELS[topPlatform] || topPlatform : '—',
      delta: topPlatform ? `${t('analyticsTab.medianViews')} · ${formatNumber(topPlatformReach)}` : t('socialMetrics.unavailable'),
      tone: 'green',
      icon: 'target',
    },
  ];
}

function formatNumber(value) {
  return formatMetric(value);
}

function formatRate(value) {
  if (value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  // Accept both 0-1 fractions and 0-100 percentages
  return n > 1 ? n : n * 100;
}

function formatRecommendationType(value = '') {
  return String(value).replace(/_/g, ' ');
}
